import React, { useState, useEffect } from "react";

interface Option {
  value: string;
  label: string;
}

interface SelectProps {
  options: Option[];
  placeholder?: string;
  onChange: (value: string) => void;
  className?: string;
  defaultValue?: string;
  value?: string;
  iconTemplate?: (value: string) => React.ReactNode;
  error?: string;
  disabled?: boolean;
  testId?: string;
}

const Select: React.FC<SelectProps> = ({
  options,
  placeholder = "Select an option",
  onChange,
  className = "",
  defaultValue = "",
  value,
  iconTemplate,
  error,
  disabled = false,
  testId
}) => {
  // Manage the selected value
  const [selectedValue, setSelectedValue] = useState<string>(defaultValue);

  // Синхронизируем внутреннее состояние с внешним значением
  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    setSelectedValue(newValue);
    onChange(newValue); // Trigger parent handler
  };

  // Используем value из пропсов, если оно предоставлено, иначе используем внутреннее состояние
  const currentValue = value !== undefined ? value : selectedValue;

  return (
    <div className="relative">
      {iconTemplate && currentValue && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2">
          {iconTemplate(currentValue)}
        </div>
      )}
      <select
        className={`h-11 w-full appearance-none rounded-lg border ${
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10'
            : 'border-border-input focus:border-brand-300 focus:ring-brand-500/10'
        } bg-bg-input ${
          iconTemplate && currentValue ? 'pl-8' : 'px-4'
        } py-2.5 pr-11 text-sm shadow-theme-xs placeholder:text-text-muted focus:outline-none focus:ring ${
          currentValue
            ? "text-text-primary"
            : "text-text-muted"
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`}
        value={currentValue}
        onChange={handleChange}
        disabled={disabled}
        data-testid={testId}
      >
        {/* Placeholder option */}
        <option
          value=""
          disabled
          className="text-text-secondary bg-bg-input"
        >
          {placeholder}
        </option>
        {/* Map over options */}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            className="text-text-secondary bg-bg-input"
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default Select;
